import type { ReactNode } from 'react';
import { TRACE_MARK } from '@traceio-org/auth-ui';
import {
  NavigationGlyph,
  type NavigationBrandConfig,
  type NavigationGlyphName,
  type NavigationGroup,
} from '@traceio-org/ui';
import { KeyGlyph } from './keyProviders';
import type { StaffAbilities } from './permissions';

export const ADMIN_PRODUCT_NAME = 'Trace Admin';

/** The staff areas not built yet (Jobs); the navigation and their pages say so. */
export const LATER_LABEL = 'Coming later';

/**
 * Trace Admin's identity in NavigationShell: the official Trace logomark (rendered through <img>, as the
 * brand package requires) and "Admin" as the wordmark. The full Trace logo already contains the mark, so it
 * would show the mark twice next to it.
 */
export const ADMIN_BRAND: NavigationBrandConfig = {
  name: ADMIN_PRODUCT_NAME,
  workspaceName: ADMIN_PRODUCT_NAME,
  mark: <img src={TRACE_MARK.peel.url} alt="" />,
  wordmark: 'Admin',
  initial: 'T',
  homeId: 'health',
  homeHref: '/',
};

/**
 * Health's gauge (Trace UI's navigation glyphs have none): drawn like the library's lucide-style glyphs (24 px box,
 * 1.65 stroke, currentColor), after lucide's gauge (ISC).
 */
export function HealthGlyph() {
  return (
    <svg
      className="trace-icon-native lucide icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  );
}

/**
 * Tickets' ticket (the library's navigation glyphs have none): the same lucide-style box, after lucide's ticket (ISC).
 */
export function TicketGlyph() {
  return (
    <svg
      className="trace-icon-native lucide icon"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2" />
      <path d="M13 17v2" />
      <path d="M13 11v2" />
    </svg>
  );
}

export type AdminSectionId =
  | 'health'
  | 'tickets'
  | 'keys'
  | 'organizations'
  | 'release-flags'
  | 'audit'
  | 'support-access'
  | 'billing'
  | 'staff-members'
  | 'jobs';

export interface AdminSection {
  id: AdminSectionId;
  label: string;
  href: string;
  /** The library glyph, or Health's and Keys' own (HealthGlyph, KeyGlyph) where the library has none. */
  glyph: NavigationGlyphName | 'Gauge' | 'Key' | 'Ticket';
  /** False for the placeholders (Jobs): their pages say so and show no data. */
  available: boolean;
  /**
   * The ability a staff member needs to see the section at all (Billing: staff.billing.read). A section without
   * one is shown to every staff member; the page itself may still refuse a read.
   */
  requires?: keyof StaffAbilities;
}

export const ADMIN_SECTIONS: readonly AdminSection[] = [
  // Health is Trace Admin's landing page (/); Organizations moved to /organizations with it (2026-10-11).
  { id: 'health', label: 'Health', href: '/', glyph: 'Gauge', available: true },
  // Tickets sits under Health (design canvas, 2026-10-13): Trace's support queue, with the Inbox count as its badge.
  {
    id: 'tickets',
    label: 'Tickets',
    href: '/tickets',
    glyph: 'Ticket',
    available: true,
    requires: 'readTickets',
  },
  // Keys sits under Health (decision of 2026-10-12): the same operational data of Trace's own hosting, every staff role.
  { id: 'keys', label: 'Keys', href: '/keys', glyph: 'Key', available: true },
  {
    id: 'organizations',
    label: 'Organizations',
    href: '/organizations',
    glyph: 'Building2',
    available: true,
  },
  {
    id: 'release-flags',
    label: 'Release flags',
    href: '/release-flags',
    glyph: 'Settings2',
    available: true,
  },
  {
    id: 'billing',
    label: 'Billing',
    href: '/billing',
    glyph: 'WalletCards',
    available: true,
    requires: 'readBilling',
  },
  {
    id: 'staff-members',
    label: 'Staff',
    href: '/staff',
    glyph: 'Users',
    available: true,
    requires: 'manageStaff',
  },
  { id: 'audit', label: 'Audit', href: '/audit', glyph: 'ShieldCheck', available: true },
  {
    id: 'support-access',
    label: 'My support access',
    href: '/support-access',
    glyph: 'Headphones',
    available: true,
  },
  { id: 'jobs', label: 'Jobs', href: '/jobs', glyph: 'Activity', available: false },
];

function icon(section: AdminSection): ReactNode {
  if (section.glyph === 'Gauge') return <HealthGlyph />;
  if (section.glyph === 'Key') return <KeyGlyph />;
  if (section.glyph === 'Ticket') return <TicketGlyph />;
  return <NavigationGlyph name={section.glyph} />;
}

/**
 * Counts shown beside sections in the navigation (Keys: the keys expired or due; Tickets: the caller's Inbox), when
 * above zero.
 */
export type NavigationBadges = Partial<Record<AdminSectionId, number>>;

/** The abilities the navigation reads (sections with `requires`). */
export type NavigationAbilities = Pick<StaffAbilities, 'readBilling' | 'manageStaff' | 'readTickets'>;

/** Whether the staff member sees a section in the navigation (its page decides nothing from this). */
export function sectionShown(section: AdminSection, abilities: NavigationAbilities): boolean {
  return section.requires === undefined || Boolean(abilities[section.requires as keyof typeof abilities]);
}

/**
 * The staff navigation for one staff member: the areas their permissions show (Billing only with
 * staff.billing.read, so a support operator never sees it), then the placeholders in their own section labeled
 * "Coming later", which open a page that says so, with no data (NavigationShell has no disabled item).
 */
export function adminNavigation(
  abilities: NavigationAbilities,
  badges: NavigationBadges = {},
): NavigationGroup[] {
  const shown = ADMIN_SECTIONS.filter((s) => sectionShown(s, abilities));
  return [
    {
      id: 'staff',
      items: shown
        .filter((s) => s.available)
        .map((s) => {
          const badge = badges[s.id] ?? 0;
          // The shell's badge is red and reads "Keys, 2 items" to assistive technology (Tickets: "Tickets, 4 items").
          return {
            id: s.id,
            label: s.label,
            href: s.href,
            icon: icon(s),
            ...(badge > 0 ? { badge, attention: true } : {}),
          };
        }),
    },
    {
      id: 'later',
      label: LATER_LABEL,
      items: shown
        .filter((s) => !s.available)
        .map((s) => ({ id: s.id, label: s.label, href: s.href, icon: icon(s) })),
    },
  ];
}

/**
 * The navigation section a path belongs to: / is Health, organization detail pages belong to Organizations, and the
 * pages under /billing (plan versions) to Billing.
 */
export function sectionForPath(pathname: string): AdminSection | null {
  if (pathname === '/') return ADMIN_SECTIONS.find((s) => s.id === 'health') ?? null;
  return (
    ADMIN_SECTIONS.find(
      (s) =>
        s.href !== '/' &&
        (pathname === s.href || pathname === `${s.href}/` || pathname.startsWith(`${s.href}/`)),
    ) ?? null
  );
}

export function sectionIcon(section: AdminSection): ReactNode {
  return icon(section);
}
